type TranslateFn = (
  key: string,
  params?: Record<string, string | number>,
  defaultValue?: string,
) => string;

type SourceReference = {
  book: string;
  page?: string;
};

const CANONICAL_SOURCE_BOOKS: Record<string, string> = {
  'Pathfinder Monster Core': 'Monster Core',
  'Pathfinder Monster Core 2': 'Monster Core 2',
  'Pathfinder NPC Core': 'NPC Core',
  'Pathfinder Player Core': 'Player Core',
  'Pathfinder Howl of the Wild': 'Howl of the Wild',
  'Pathfinder Battlecry!': 'Battlecry!',
  'Pathfinder Claws of the Tyrant': 'Claws of the Tyrant',
  'Pathfinder Dark Archive (Remastered)': 'Dark Archive (Remastered)',
  'Pathfinder War of Immortals': 'War of Immortals',
  'Pathfinder Season of Ghosts Hardcover Compilation': 'Season of Ghosts',
  'Pathfinder Adventure Path: Gatewalkers': 'Gatewalkers',
  'Pathfinder Adventure Path: Hellbreakers': 'Adventure Path #222: Hellbreakers',
  'Pathfinder Lost Omens Draconic Codex': 'Lost Omens Draconic Codex',
  'Pathfinder Lost Omens High Seas': 'High Seas',
  'Pathfinder Lost Omens Shining Kingdoms': 'Lost Omens Shining Kingdoms',
  'Pathfinder Lost Omens Tian Xia World Guide': 'Lost Omens Tian Xia World Guide',
  'Pathfinder #208: Hoof, Cinder, and Storm': 'Adventure Path #208: Hoof, Cinder, and Storm',
  'Pathfinder #210: Whispers in the Dirt': 'Adventure Path #210: Whispers in the Dirt',
  'Pathfinder #211: The Secret of Deathstalk Tower': 'Adventure Path #211: The Secret of Deathstalk Tower',
  'Pathfinder #212: A Voice in the Blight': 'Adventure Path #212: A Voice in the Blight',
  'Pathfinder #213: Thirst for Blood': 'Adventure Path #213: Thirst for Blood',
  'Pathfinder #214: The Broken Palace': 'Adventure Path #214: The Broken Palace',
  'Pathfinder #215: To Blot Out the Sun': 'Adventure Path #215: To Blot Out the Sun',
  'Pathfinder #216: The Acropolis Pyre': 'Adventure Path #216: The Acropolis Pyre',
  'Pathfinder #217: Death Sails a Wine-Dark Sea': 'Adventure Path #217: Death Sails a Wine-Dark Sea',
  'Pathfinder #218: Titanbane': 'Adventure Path #218: Titanbane',
  'Pathfinder #219: Lord of the Trinity Star': 'Adventure Path #219: Lord of the Trinity Star',
  'Pathfinder #220: Crypt of Runes': 'Adventure Path #220: Crypt of Runes',
  'Pathfinder Adventures: Dark Archive Web Supplement: In Darkness (Remastered)': 'Dark Archive: In Darkness (Remastered)',
  'Pathfinder Society Quest #27: Within the Glacier': 'Pathfinder Quest (Series 2) #27: Within the Glacier',
  'Dragon Codex': 'Lost Omens Draconic Codex',
  'Dark Archives': 'Dark Archive (Remastered)',
  'Dark Archives (Remastered)': 'Dark Archive (Remastered)',
  'Guns & Gears (Remastered)': 'Guns & Gears',
};

const KNOWN_COMMA_TITLES = [
  'Absalom, City of Lost Omens',
  'Pathfinder var(--m13-color-205): Singer, Stalker, Skinsaw Man',
  'Pathfinder #208: Hoof, Cinder, and Storm',
  'PFS Scenario #1-24: Lightning Strikes, Stars Fall',
];

const COMMA_MARKER = '__PF2E_COMMA__';

function cleanSourceText(raw: string | null | undefined): string {
  return (raw || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, (_, label: string) => label)
    .replace(/\\r\\n|\\n|\\r/g, ' ')
    .replace(/\r?\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function protectKnownCommaTitles(text: string): string {
  return KNOWN_COMMA_TITLES.reduce(
    (result, title) => result.split(title).join(title.split(',').join(COMMA_MARKER)),
    text,
  );
}

function restoreKnownCommaTitles(text: string): string {
  return text.split(COMMA_MARKER).join(',');
}

export function canonicalizeSourceBook(raw: string | null | undefined): string {
  const cleaned = restoreKnownCommaTitles(cleanSourceText(raw))
    .replace(/\s+pg\.?$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  return CANONICAL_SOURCE_BOOKS[cleaned] || cleaned;
}

function toCamelKey(raw: string): string {
  const normalized = raw
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim();

  if (!normalized) return '';

  const words = normalized.split(/\s+/);
  return words[0].toLowerCase() + words.slice(1).map((word) => (
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  )).join('');
}

function parseSourcePart(part: string): SourceReference | null {
  const restored = restoreKnownCommaTitles(part).trim();
  if (!restored) return null;

  const match = restored.match(/^(.*?)\s+pg\.?\s*(\d+(?:[-\u2013]\d+)?)\b/i);
  const book = canonicalizeSourceBook(match ? match[1] : restored);
  if (!book) return null;

  return {
    book,
    page: match?.[2],
  };
}

function parseSourceText(raw: string | null | undefined): SourceReference[] {
  const cleaned = cleanSourceText(raw);
  if (!cleaned) return [];

  return protectKnownCommaTitles(cleaned)
    .split(/\s*,\s*/)
    .map(parseSourcePart)
    .filter((source): source is SourceReference => Boolean(source));
}

export function sourceBookLocaleKey(raw: string | null | undefined): string {
  const book = canonicalizeSourceBook(raw);
  const key = toCamelKey(book);
  return key ? `pf2e.sourceBooks.${key}` : '';
}

export function translateSourceBook(
  raw: string | null | undefined,
  t: TranslateFn,
): string {
  const book = canonicalizeSourceBook(raw);
  if (!book) return '';

  const key = sourceBookLocaleKey(book);
  if (!key) return book;

  const translated = t(key, {}, book);
  return translated && translated !== key ? translated : book;
}

export function translateSourceText(
  raw: string | null | undefined,
  t: TranslateFn,
): string {
  const sources = parseSourceText(raw);
  if (!sources.length) return '';

  const seen = new Set<string>();
  return sources
    .map((source) => {
      const book = translateSourceBook(source.book, t);
      const page = source.page ? `${t('character_creation.pageIndicator', {}, 'pg.')} ${source.page}` : '';
      return [book, page].filter(Boolean).join(' ');
    })
    .filter((label) => {
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    })
    .join(', ');
}

export function formatSourceWithPage(
  source: string | null | undefined,
  sourcePg: string | number | null | undefined,
  t: TranslateFn,
): string {
  const cleaned = cleanSourceText(source);
  if (!cleaned && (sourcePg === null || sourcePg === undefined || sourcePg === '')) return '';
  if (/\bpg\.?\s*\d+/i.test(cleaned)) return translateSourceText(cleaned, t);

  const book = translateSourceBook(cleaned, t);
  const page = sourcePg !== null && sourcePg !== undefined && sourcePg !== ''
    ? `${t('character_creation.pageIndicator', {}, 'pg.')} ${sourcePg}`
    : '';

  return [book, page].filter(Boolean).join(' ');
}
